import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import DetailsIcon from '@material-ui/icons/Search';
import EnhancedTableHead from '../EnhancedTableHead';
import useColumnFilters from 'src/hooks/useColumnFilters';

const useStyles = makeStyles({
  tableCell: {
    cursor: 'pointer',
  },
  loader: {
    position: 'absolute !important',
    top: '20px',
    left: '45%',
  },
  tableBody: {
    position: 'relative',
    height: '60px',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
});

const headCells = [
  { id: 'date', numeric: false, disablePadding: false, label: 'Date', sortable: true },
  {
    id: 'type',
    numeric: false,
    disablePadding: false,
    label: 'Type',
    sortable: true,
  },
  {
    id: 'description',
    numeric: false,
    disablePadding: false,
    label: 'Description',
    sortable: true,
  },
  {
    id: 'ledger_value',
    numeric: false,
    disablePadding: false,
    label: 'Value',
    sortable: true,
  },
  {
    id: 'balance',
    numeric: false,
    disablePadding: false,
    label: 'Balance After',
    sortable: true,
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
    sortable: true,
  },
  {
    id: 'order_id',
    numeric: false,
    disablePadding: false,
    label: 'Order Id',
    sortable: true,
  },
  {
    id: 'invoice_url',
    numeric: false,
    disablePadding: false,
    label: 'Invoice',
    sortable: false,
  },
  {
    id: 'action',
    numeric: false,
    disablePadding: false,
    label: 'Action',
    sortable: false,
  },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const formatMoney = (value, signed = false) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numberValue = Number(value);
  const sign = signed && numberValue > 0 ? '+' : '';
  return `${sign}${numberValue.toFixed(2)}€`;
};

const PaymentsListResults = ({
  payments,
  handleRow,
  loading,
  pagerInfo,
  handlePagerInfo,
  ...rest
}) => {
  const classes = useStyles();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const { filters, onFilterChange, applyFilters } = useColumnFilters();
  const navigate = useNavigate();

  const handleLimitChange = (event) => {
    handlePagerInfo({
      limit: event.target.value,
      page: 1,
      totalSize: 0,
    });
  };

  const handlePageChange = (event, newPage) => {
    handlePagerInfo({
      ...pagerInfo,
      page: newPage + 1,
    });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const goToDetails = (id) => {
    navigate(`/app/payments/details/${id}`, { state: id });
  };

  return (
    <>
      <Card {...rest}>
        <PerfectScrollbar>
          <Box sx={{ minWidth: 1050 }}>
            <Table>
              <EnhancedTableHead
                classes={classes}
                order={order}
                orderBy={orderBy}
                headCells={headCells}
                onRequestSort={handleRequestSort}
              filters={filters}
              onFilterChange={onFilterChange}
            />
              <TableBody className={classes.tableBody}>
                {loading && <CircularProgress className={classes.loader} />}
                {!loading &&
                  payments &&
                  stableSort(applyFilters(payments), getComparator(order, orderBy)).map(
                    (user) => (
                      <TableRow hover key={user.id}>
                        <TableCell>{user.date}</TableCell>
                        <TableCell>{user.type}</TableCell>
                        <TableCell>{user.description}</TableCell>
                        <TableCell>
                          <Typography
                            color={Number(user.ledger_value) < 0 ? 'error' : 'primary'}
                          >
                            {formatMoney(user.ledger_value, true)}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatMoney(user.balance)}</TableCell>
                        <TableCell>{user.status}</TableCell>
                        <TableCell>{user.order_id || ''}</TableCell>
                        {user.invoice_url ? (
                          <TableCell>
                            <a
                              href={user.invoice_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              INVOICE
                            </a>
                          </TableCell>
                        ) : (
                          <TableCell />
                        )}
                        <TableCell>
                          {user.order_id && (
                            <DetailsIcon
                              style={{ cursor: 'pointer' }}
                              onClick={() => goToDetails(user.order_id)}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                {!loading && !payments && (
                  <p className={classes.loader}>
                    <Typography>No Data</Typography>
                  </p>
                )}
              </TableBody>
            </Table>
          </Box>
        </PerfectScrollbar>
        <TablePagination
          component="div"
          count={pagerInfo ? pagerInfo.totalSize : 0}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
          page={pagerInfo ? pagerInfo.page - 1 : 0}
          rowsPerPage={pagerInfo ? pagerInfo.limit : 0}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </Card>
    </>
  );
};

PaymentsListResults.propTypes = {
  payments: PropTypes.array,
  handleRow: PropTypes.func,
  loading: PropTypes.bool,
  pagerInfo: PropTypes.object,
  handlePagerInfo: PropTypes.func,
};

export default PaymentsListResults;
